import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Verify email landing page — auto-fires GET /api/auth/verify-email?token=...
 * on mount, then shows the result. Idempotent server-side so a page reload
 * won't double-consume anything.
 */
export default function VerifyEmail() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // "checking" | "verified" | "already" | "failed"
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        if (alive) { setState("failed"); setErr("Missing verification token."); }
        return;
      }
      try {
        const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        if (!alive) return;
        if (data.already_verified) setState("already");
        else if (data.verified) setState("verified");
        else setState("failed");
      } catch (e) {
        if (!alive) return;
        setState("failed");
        setErr(e?.response?.data?.detail || "This verification link is invalid or has expired.");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#f4f6f8] grid place-items-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#e6edf1] shadow-sm p-6 sm:p-8 text-center" data-testid="verify-email-page">
        {state === "checking" && (
          <>
            <Loader2 size={28} className="animate-spin mx-auto text-[#457b9d] mb-4" />
            <h1 className="text-[18px] font-bold text-[#1d3557]">Verifying your email…</h1>
            <p className="text-sm text-[#667085] mt-2">This will only take a moment.</p>
          </>
        )}
        {(state === "verified" || state === "already") && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#22c55e]/15 text-[#16a34a] grid place-items-center mx-auto mb-4">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="text-[20px] font-bold text-[#1d3557]" data-testid="verify-success">
              {state === "already" ? "Already verified" : "Email verified"}
            </h1>
            <p className="text-sm text-[#667085] leading-relaxed mt-2">
              {state === "already"
                ? "Your workshop's email is already confirmed. You're all set."
                : "Thank you — your workshop's email is now confirmed and your Fender account is fully active."}
            </p>
            <Button
              onClick={() => nav("/login")}
              className="mt-6 bg-[#e63946] hover:bg-[#d02f3c] text-white"
              data-testid="verify-goto-login"
            >
              Continue to Fender <ArrowRight size={14} className="ml-1" />
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#e63946]/12 text-[#e63946] grid place-items-center mx-auto mb-4">
              <XCircle size={28} />
            </div>
            <h1 className="text-[20px] font-bold text-[#1d3557]" data-testid="verify-failed">
              Couldn&apos;t verify email
            </h1>
            <p className="text-sm text-[#667085] leading-relaxed mt-2">{err}</p>
            <div className="flex items-start gap-2 p-3 mt-4 rounded-md bg-[#F79009]/10 text-[#B54708] text-left">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div className="text-xs leading-relaxed">
                If the link has expired, ask the workshop owner to log in and
                request a new verification email from Settings.
              </div>
            </div>
            <Link
              to="/login"
              className="inline-block mt-6 text-sm text-[#e63946] font-semibold"
              data-testid="verify-goto-login-failed"
            >
              Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
